import React, { useEffect, useRef, useState } from "react";
import { webLoadingFalse } from "../../services/navigate.service";
import { getLocations } from "../../services/locations.services";
import { getGeneralLedgerReport } from "../../services/report.service";
import { useResponsive } from "../../hook/useResponsive";
import GeneralReportIcon from "../../images/GeneralReportIcon";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListScreen,
  TEXT,
  TEXT_DIM,
  cells,
  formatMoney,
  t,
} from "../../components/list";

interface GeneralLedgerRow {
  glid: string;
  tran: string | number | null;
  Reference: string | null;
  accountcode: string | null;
  accountname: string | null;
  CompanyName: string | null;
  debit: string | null;
  credit: string | null;
  description: string | null;
}

interface GeneralLedgerResponse {
  count: number;
  data: GeneralLedgerRow[];
}

const toAmount = (value: string | null | undefined) =>
  value === null || value === undefined || value === "" ? null : Number(value);

const GeneralReport = () => {
  const { isTouchLayout } = useResponsive();
  const companyId = localStorage.getItem("companyId");
  const userId = localStorage.getItem("userId");
  const [language, setLanguage] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const inforTable: any = {
    img: <GeneralReportIcon />,
    title:
      language && language?.["No general report yet"]
        ? language?.["No general report yet"]
        : "No general report yet",
    desc:
      language &&
        language?.[
        "Your general reports will be displayed here after you add invoices."
        ]
        ? language?.[
        "Your general reports will be displayed here after you add invoices."
        ]
        : "Your general reports will be displayed here after you add invoices.",
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const getListLocations = async () => {
    try {
      const response: any = await getLocations(userId, companyId, "", "", "");
      setLocations(response.data.data ?? []);
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };
  useEffect(() => {
    getListLocations();
    document.addEventListener("newToken", getListLocations);
    return () => {
      document.removeEventListener("newToken", getListLocations);
    };
  }, []);

  /** One page of ledger lines; the API searches txn no., account name and account code. */
  const fetchGeneralLedger = async (query: ListQuery) => {
    webLoadingFalse();
    const response = await getGeneralLedgerReport(
      query.page,
      query.limit,
      String(query.search ?? "").trim().replace(/^#/, ""),
      companyId,
      query.filters.type,
      query.filters.location,
    );
    const value: GeneralLedgerResponse = response.data;
    return { rows: value.data ?? [], count: value.count ?? 0 };
  };

  /** A GL line posts to one side: the phone card shows that amount and names the side. */
  const postedAmount = (row: GeneralLedgerRow) => {
    const debit = Number(row.debit) || 0;
    const credit = Number(row.credit) || 0;
    if (debit !== 0) return { value: debit, side: "Debit" };
    if (credit !== 0) return { value: credit, side: "Credit" };
    return { value: 0, side: "" };
  };

  const columns: ListColumn<GeneralLedgerRow>[] = [
    {
      key: "tran",
      label: "Tran",
      width: 90,
      hideable: false,
      value: (r) =>
        r.tran === null || r.tran === undefined || r.tran === "" ? "" : "#" + r.tran,
      mobile: "subtitle",
    },
    {
      key: "CompanyName",
      label: "Company Name",
      width: 180,
      value: (r) => r.CompanyName ?? "",
      mobile: "sheet",
    },
    {
      key: "Reference",
      label: "Reference",
      width: 140,
      value: (r) => r.Reference ?? "",
      mobile: "subtitle",
    },
    {
      key: "accountcode",
      label: "Acc. Code",
      width: 100,
      value: (r) => r.accountcode ?? "",
      mobile: "sheet",
    },
    {
      key: "accountname",
      label: "Account Name",
      width: 200,
      minWidth: 140,
      value: (r) => r.accountname || r.accountcode || "",
      mobile: "title",
    },
    {
      key: "debit",
      label: "Debit",
      width: 120,
      minWidth: 96,
      align: "right",
      render: cells.money((r) => toAmount(r.debit)),
      mobile: "sheet",
    },
    {
      key: "credit",
      label: "Credit",
      width: 120,
      minWidth: 96,
      align: "right",
      render: cells.money((r) => toAmount(r.credit)),
      mobile: "sheet",
    },
    {
      key: "description",
      label: "Description",
      width: 280,
      minWidth: 160,
      value: (r) => r.description ?? "",
      mobile: "subtitle",
    },
    {
      key: "postedAmount",
      label: "Amount",
      width: 120,
      desktop: false,
      mobile: "trailing",
      render: (r) => {
        const { value, side } = postedAmount(r);
        return (
          <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
            <div
              className="font-inter"
              style={{ fontSize: "14px", fontWeight: 500, color: TEXT, fontVariantNumeric: "tabular-nums" }}
            >
              {formatMoney(value)}
            </div>
            {side && (
              <div className="font-inter" style={{ fontSize: "11.5px", color: TEXT_DIM }}>
                {t(language, side, side)}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const filters: ListFilter[] = [
    {
      key: "type",
      label: "Type",
      kind: "select",
      options: [
        { value: "sales", label: t(language, "Sales", "Sales") },
        { value: "purchase", label: t(language, "Purchases", "Purchases") },
      ],
    },
    {
      key: "location",
      label: "Location",
      kind: "select",
      options: locations.map((l: any) => ({ value: String(l?.id), label: l?.name ?? "" })),
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: isTouchLayout ? "auto" : undefined,
        paddingBottom: isTouchLayout ? "24px" : undefined,
      }}
    >
      <div>
        <p
          style={{ color: "#222525" }}
          className="text-[24px] leading-[130%] font-medium font-inter"
        >
          {language && language?.["General Ledger Report"]
            ? language?.["General Ledger Report"]
            : "General Ledger Report"}
        </p>
      </div>
      <ListScreen
        language={language}
        columns={columns}
        fetchRows={fetchGeneralLedger}
        rowKey={(r: GeneralLedgerRow) => String(r.glid)}
        searchPlaceholder="Search transaction, account or code"
        searchInputRef={searchInputRef}
        filters={filters}
        countNoun="entries"
        emptyState={inforTable}
      />
    </div>
  );
};

export default GeneralReport;
